import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import AnimatedSection from '../../components/base/AnimatedSection';

const BusinessPage = () => {
  const [expandedFeasibility, setExpandedFeasibility] = useState<number | null>(null);
  const [selectedMarketingStep, setSelectedMarketingStep] = useState<number | null>(null);
  const [hoveredStep, setHoveredStep] = useState<number | null>(null);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: '부동산 개발 사업 서비스',
    provider: {
      '@type': 'RealEstateAgent',
      name: '주식회사 GB더플래닛',
    },
    description: '입지조사, 사업성 분석, 사업기획, 분양, 마케팅, 관리까지 부동산 개발 전 과정을 수행합니다',
    areaServed: {
      '@type': 'Country',
      name: '대한민국',
    },
    serviceType: ['입지조사', '사업성 분석', '사업기획', '분양대행', '마케팅', '업무대행'],
    url: typeof window !== 'undefined' ? `${window.location.origin}/business` : '',
  };

  return (
    <div className="min-h-screen bg-white">
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <Navbar />

      <div className="pt-20">
        {/* 1. BUSINESS OVERVIEW */}
        <section className="relative bg-navy py-12 md:py-20 overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/497e59b14b7431b713e8d24fba7b76dc.jpg"
              alt="Business Background"
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-navy/80 via-navy/70 to-navy/90"></div>
          </div>

          <div className="relative z-10 max-w-[1200px] mx-auto px-4 md:px-8">
            <div className="flex items-center justify-center">
              <AnimatedSection animation="fadeUp">
                <div className="text-center">
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight mb-3 md:mb-4">
                    사업의 시작부터 분양 완료까지,<br />
                    <span className="text-gold">GB더플래닛의 실행 중심 프로세스</span>
                  </h1>
                  <p className="text-base md:text-lg text-white/80 leading-relaxed">
                    입지조사 · 사업성 분석 · 사업기획 · 분양 · 마케팅 · 관리
                  </p>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* 2. WORK PROCESS */}
        <section className="bg-white py-12 md:py-20">
          <div className="max-w-[1200px] mx-auto px-4 md:px-8">
            <AnimatedSection animation="fadeUp">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy mb-2 md:mb-3 text-center font-english">WORK PROCESS</h2>
              <p className="text-center text-xs md:text-sm text-gray-600 mb-8 md:mb-12">체계적인 5단계 프로세스로 사업을 완성합니다</p>
            </AnimatedSection>

            <div className="relative">
              <div className="hidden lg:block absolute top-[120px] left-0 right-0 px-[100px] z-0">
                <div className="flex items-center">
                  {[0, 1, 2, 3].map((index) => (
                    <div key={index} className="flex-1 flex items-center">
                      <div 
                        className={`h-0.5 flex-1 rounded-full transition-all duration-500 ${
                          hoveredStep !== null && hoveredStep > index 
                            ? 'bg-gold shadow-[0_0_10px_rgba(212,175,55,0.5)]' 
                            : 'bg-gray-200'
                        }`}
                      />
                      <div 
                        className={`w-2.5 h-2.5 rounded-full transition-all duration-500 flex-shrink-0 ${
                          hoveredStep !== null && hoveredStep > index 
                            ? 'bg-gold shadow-[0_0_10px_rgba(212,175,55,0.5)] scale-125' 
                            : 'bg-gray-300'
                        }`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2.5 md:gap-3 lg:gap-4 relative z-10">
                {[
                  {
                    step: '01',
                    icon: 'ri-map-pin-line',
                    title: '입지조사',
                    subtitle: 'Site / Situation 분석',
                    items: ['토지이용 조사', '인구 분석', '도시계획 검토'],
                    preview: ['상권 분석', '교통 접근성', '개발 호재 검토', '경쟁 환경 파악'],
                  },
                  {
                    step: '02',
                    icon: 'ri-line-chart-line',
                    title: '분석 및 기획',
                    subtitle: '사업성 · 권리 · 경영 분석',
                    items: ['사업수지 분석', '자금 계획', '리스크 평가'],
                    preview: ['수익률 시뮬레이션', '법적 검토', '투자 타당성', '시나리오 분석'],
                  },
                  {
                    step: '03',
                    icon: 'ri-building-line',
                    title: '사업방식 제안',
                    subtitle: '최적 사업 구조 제안',
                    items: ['단독사업', '지주공동', '조합/재개발'],
                    preview: ['사업 구조 설계', '파트너십 구성', '리스크 분산', '수익 배분 계획'],
                  },
                  {
                    step: '04',
                    icon: 'ri-advertisement-line',
                    title: '부동산 마케팅',
                    subtitle: '분양·임대 전략 수립',
                    items: ['분양 기획', '마케팅 전략', '영업 관리'],
                    preview: ['타겟 고객 분석', '채널 전략', '프로모션 기획', '세일즈 교육'],
                  },
                  {
                    step: '05',
                    icon: 'ri-team-line',
                    title: '아웃소싱 관리',
                    subtitle: '인·허가 대관',
                    items: ['시공사 관리', '협력사 감독', '준공 관리'],
                    preview: ['품질 관리', '공정 모니터링', '하자 점검', '입주 지원'],
                  },
                ].map((item, index) => (
                  <AnimatedSection key={index} animation="fadeUp" delay={index * 100}>
                    <div 
                      className={`relative h-[200px] md:h-[220px] lg:h-[260px] group perspective-1000 ${index === 4 ? 'col-span-2 md:col-span-3 lg:col-span-1 max-w-[200px] md:max-w-none mx-auto md:mx-0' : ''}`}
                      onMouseEnter={() => setHoveredStep(index)}
                      onMouseLeave={() => setHoveredStep(null)}
                    >
                      <div 
                        className={`hidden lg:block absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full border-2 transition-all duration-500 z-20 ${
                          hoveredStep !== null && hoveredStep >= index 
                            ? 'bg-gold border-gold shadow-[0_0_15px_rgba(212,175,55,0.6)] scale-125' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                      
                      <div className={`absolute inset-0 bg-gray-50 rounded-xl p-2.5 md:p-3 lg:p-4 border transition-all duration-500 group-hover:opacity-0 group-hover:scale-95 ${
                        hoveredStep !== null && hoveredStep >= index 
                          ? 'border-gold/50 shadow-lg' 
                          : 'border-gray-100'
                      }`}>
                        <div className="text-xl md:text-2xl lg:text-3xl font-bold text-gold/20 mb-1.5 md:mb-2">STEP {item.step}</div>
                        <div className={`w-8 h-8 md:w-9 md:h-9 lg:w-11 lg:h-11 flex items-center justify-center rounded-lg mb-1.5 md:mb-2 lg:mb-3 transition-colors duration-500 ${
                          hoveredStep !== null && hoveredStep >= index 
                            ? 'bg-gold/20' 
                            : 'bg-gold/10'
                        }`}>
                          <i className={`${item.icon} text-lg md:text-xl lg:text-2xl text-gold`}></i>
                        </div>
                        <h3 className="text-sm md:text-base lg:text-lg font-bold text-navy mb-0.5 md:mb-1 break-words">{item.title}</h3>
                        <p className="text-[10px] md:text-xs text-gray-500 mb-1.5 md:mb-2 lg:mb-3 break-words">{item.subtitle}</p>
                        <ul className="space-y-0.5 md:space-y-1">
                          {item.items.map((subItem, subIndex) => (
                            <li key={subIndex} className="text-[10px] md:text-xs text-gray-600 flex items-center gap-1 md:gap-1.5">
                              <span className="w-1 h-1 bg-gold rounded-full flex-shrink-0"></span>
                              <span className="break-words">{subItem}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="absolute inset-0 bg-navy rounded-xl p-2.5 md:p-3 lg:p-4 transition-all duration-500 opacity-0 scale-105 group-hover:opacity-100 group-hover:scale-100 overflow-hidden">
                        <div className="absolute top-0 right-0 w-16 md:w-20 lg:w-24 h-16 md:h-20 lg:h-24 bg-gold/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                        <div className="absolute bottom-0 left-0 w-12 md:w-16 lg:w-20 h-12 md:h-16 lg:h-20 bg-gold/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                        
                        <div className="relative z-10">
                          <div className="flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2 lg:mb-3">
                            <div className="w-6 h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 flex items-center justify-center bg-gold rounded-lg">
                              <i className={`${item.icon} text-sm md:text-base lg:text-lg text-navy`}></i>
                            </div>
                            <div>
                              <span className="text-gold text-[10px] md:text-xs font-semibold">STEP {item.step}</span>
                              <h3 className="text-xs md:text-sm lg:text-base font-bold text-white break-words">{item.title}</h3>
                            </div>
                          </div>

                          <div className="mb-1.5 md:mb-2">
                            <span className="text-[10px] md:text-xs text-gold/80 font-medium">상세 업무 내용</span>
                          </div>

                          <ul className="space-y-1 md:space-y-1.5">
                            {item.preview.map((previewItem, previewIndex) => (
                              <li 
                                key={previewIndex} 
                                className="text-[10px] md:text-xs text-white/90 flex items-center gap-1 md:gap-1.5 transform transition-all duration-300"
                                style={{ transitionDelay: `${previewIndex * 50}ms` }}
                              >
                                <i className="ri-arrow-right-s-line text-gold text-xs md:text-sm flex-shrink-0"></i>
                                <span className="break-words">{previewItem}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 3. BUSINESS FEASIBILITY */}
        <section className="bg-[#FAF9F6] py-12 md:py-20">
          <div className="max-w-[1200px] mx-auto px-4 md:px-8">
            <AnimatedSection animation="fadeUp">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy mb-2 md:mb-3 text-center">사업성 평가</h2>
              <p className="text-base md:text-lg text-gray-600 mb-2 text-center">
                사업 성공 가능성을 판단하는 것이 아니라
              </p>
              <p className="text-base md:text-lg text-navy font-bold mb-10 md:mb-12 text-center">
                성공 가능성을 극대화하는 구조를 설계합니다.
              </p>
            </AnimatedSection>

            {/* Feasibility Diagram */}
            <AnimatedSection animation="fadeUp" delay={200}>
              <div className="bg-white rounded-2xl p-6 md:p-10 shadow-lg mb-8 md:mb-10">
                <div className="flex flex-col lg:flex-row items-center justify-center gap-8 md:gap-12 lg:gap-16">
                  {/* Left Side - Process Flow */}
                  <div className="flex items-center gap-4 md:gap-6">
                    {/* Column 1: 고객 → 의뢰 → 조사 */}
                    <div className="flex flex-col gap-3 md:gap-4">
                      <div className="w-16 h-16 md:w-20 md:h-20 bg-[#C4A574] rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm md:text-base">고객</span>
                      </div>
                      <div className="w-0.5 h-6 md:h-8 bg-gray-300 mx-auto"></div>
                      <div className="w-16 h-16 md:w-20 md:h-20 bg-[#C4A574] rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm md:text-base">의뢰</span>
                      </div>
                      <div className="w-0.5 h-6 md:h-8 bg-gray-300 mx-auto"></div>
                      <div className="w-16 h-16 md:w-20 md:h-20 bg-[#C4A574] rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm md:text-base">조사</span>
                      </div>
                    </div>

                    {/* Column 2: Survey Details */}
                    <div className="flex flex-col gap-3 md:gap-4">
                      <div className="h-16 md:h-20"></div>
                      <div className="h-6 md:h-8"></div>
                      <div className="flex flex-col gap-2 md:gap-3">
                        <div className="bg-white border-2 border-gray-200 rounded-lg px-3 md:px-4 py-2 md:py-2.5">
                          <div className="text-xs md:text-sm font-bold text-navy mb-1">물건조사</div>
                          <div className="text-[10px] md:text-xs text-gray-600">용도, 면적, 도로폭, 건폐율, 용적률 등</div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <div className="bg-white border-2 border-gray-200 rounded-lg px-3 md:px-4 py-2 md:py-2.5 flex-1">
                            <div className="text-xs md:text-sm font-bold text-navy">입지조사</div>
                          </div>
                          <div className="bg-white border-2 border-gray-200 rounded-lg px-3 md:px-4 py-2 md:py-2.5">
                            <div className="text-xs md:text-sm font-bold text-navy">법적규제조사</div>
                          </div>
                        </div>
                        <div className="text-[10px] md:text-xs text-gray-600 px-2">
                          도시계획법, 건축법, 주고 조정 및 주위건물과의 조정, 상하수도 문제
                        </div>

                        <div className="bg-white border-2 border-gray-200 rounded-lg px-3 md:px-4 py-2 md:py-2.5">
                          <div className="text-xs md:text-sm font-bold text-navy mb-1">개발타당성 조사</div>
                          <div className="text-[10px] md:text-xs text-gray-600">도입입지(Site, Situation), 주변시설, 자연환경(해, 경관), 도시관 조사</div>
                        </div>

                        <div className="bg-white border-2 border-gray-200 rounded-lg px-3 md:px-4 py-2 md:py-2.5">
                          <div className="text-xs md:text-sm font-bold text-navy mb-1">시장조사</div>
                          <div className="text-[10px] md:text-xs text-gray-600">부동산 시장 등</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Side - Analysis & Planning */}
                  <div className="flex items-center gap-4 md:gap-6">
                    {/* Column 3: 자료정리 → 분석 및 기획 */}
                    <div className="flex flex-col gap-3 md:gap-4 items-center">
                      <div className="w-16 h-16 md:w-20 md:h-20 bg-navy rounded-full flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-white font-bold text-xs md:text-sm">자료</div>
                          <div className="text-white font-bold text-xs md:text-sm">정리</div>
                        </div>
                      </div>
                      <div className="w-0.5 h-6 md:h-8 bg-gray-300"></div>
                      <div className="w-20 h-20 md:w-24 md:h-24 bg-navy rounded-full flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-white font-bold text-xs md:text-sm">분석 및</div>
                          <div className="text-white font-bold text-xs md:text-sm">기획</div>
                        </div>
                      </div>
                    </div>

                    {/* Column 4: Analysis Details */}
                    <div className="flex flex-col gap-2 md:gap-3">
                      <div className="bg-white border-2 border-gray-200 rounded-lg px-3 md:px-4 py-2 md:py-2.5">
                        <div className="text-xs md:text-sm font-bold text-navy">권리분석</div>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="bg-white border-2 border-gray-200 rounded-lg px-3 md:px-4 py-2 md:py-2.5 flex-1">
                          <div className="text-xs md:text-sm font-bold text-navy">마케팅 및 위험성 분석</div>
                        </div>
                        <div className="bg-white border-2 border-gray-200 rounded-lg px-3 md:px-4 py-2 md:py-2.5">
                          <div className="text-xs md:text-sm font-bold text-navy">경영분석</div>
                        </div>
                      </div>

                      <div className="bg-white border-2 border-gray-200 rounded-lg px-3 md:px-4 py-2 md:py-2.5">
                        <div className="text-xs md:text-sm font-bold text-navy">사업수지계획</div>
                      </div>

                      <div className="bg-white border-2 border-gray-200 rounded-lg px-3 md:px-4 py-2 md:py-2.5">
                        <div className="text-xs md:text-sm font-bold text-navy">자금세무계획</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Expandable Details */}
            <div className="space-y-2 md:space-y-3">
              {[
                {
                  title: '조사',
                  icon: 'ri-search-2-line',
                  items: [
                    { name: '물건조사', desc: '용도, 면적, 도로폭, 건폐율, 용적률 등' },
                    { name: '입지조사', desc: '법적 규제, 도시계획, 개발 가능성' },
                    { name: '개발타당성 조사', desc: '기술적·경제적 실현 가능성' },
                    { name: '시장조사', desc: '수요 예측, 경쟁 환경 분석' },
                  ],
                },
                {
                  title: '분석 및 기획',
                  icon: 'ri-pie-chart-line',
                  items: [
                    { name: '권리분석', desc: '소유권, 저당권, 임차권 등 법적 권리 검토' },
                    { name: '경영분석', desc: '수익성, 현금흐름, ROI 분석' },
                    { name: '마케팅 및 위험성 분석', desc: '시장 리스크, 분양 전략' },
                    { name: '사업수지 계획', desc: '총 사업비, 예상 수익 산출' },
                    { name: '자금 세부 계획', desc: '자금 조달 방안 및 집행 계획' },
                  ],
                },
              ].map((section, index) => (
                <AnimatedSection key={index} animation="fadeUp" delay={index * 100}>
                  <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
                    <button
                      onClick={() => setExpandedFeasibility(expandedFeasibility === index ? null : index)}
                      className="w-full px-4 md:px-6 py-3 md:py-4 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-2 md:gap-3">
                        <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-gold/10 rounded-lg">
                          <i className={`${section.icon} text-lg md:text-xl text-gold`}></i>
                        </div>
                        <h3 className="text-lg md:text-xl font-bold text-navy">{section.title}</h3>
                      </div>
                      <i
                        className={`ri-arrow-down-s-line text-lg md:text-xl text-gray-400 transition-transform ${
                          expandedFeasibility === index ? 'rotate-180' : ''
                        }`}
                      ></i>
                    </button>

                    {expandedFeasibility === index && (
                      <div className="px-4 md:px-6 pb-4 md:pb-6 pt-2 md:pt-3 border-t border-gray-100">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                          {section.items.map((item, itemIndex) => (
                            <div key={itemIndex} className="flex gap-2">
                              <div className="w-1.5 h-1.5 bg-gold rounded-full mt-1.5 flex-shrink-0"></div>
                              <div>
                                <h4 className="font-bold text-navy mb-0.5 text-sm">{item.name}</h4>
                                <p className="text-xs text-gray-600">{item.desc}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* 4. SALE / BUSINESS AGENT */}
        <section className="bg-white py-12 md:py-20">
          <div className="max-w-[1200px] mx-auto px-4 md:px-8">
            <AnimatedSection animation="fadeUp">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy mb-2 md:mb-3 text-center">분양 및 업무대행</h2>
              <p className="text-center text-xs md:text-sm text-gray-600 mb-8 md:mb-12">전략적 기획부터 실질적 영업까지</p>
            </AnimatedSection>

            {/* 모바일: 삼각형 배치 (상단 2개 + 하단 1개 중앙) */}
            <div className="flex flex-col items-center gap-2.5 md:hidden">
              <div className="grid grid-cols-2 gap-2.5 w-full">
                {[
                  {
                    en: 'PLANNING',
                    ko: '전략기획',
                    icon: 'ri-lightbulb-flash-line',
                    desc: '합리적인 분양 전략 수립',
                    subdesc: '인·허가 조건을 고려한 사업 기획',
                    items: ['시장 분석 및 타겟 설정', '분양가 전략', '차별화 포인트 발굴', '분양 일정 수립'],
                  },
                  {
                    en: 'CREATIVE',
                    ko: '구성기획',
                    icon: 'ri-palette-line',
                    desc: '상품 경쟁력 강화',
                    subdesc: '고객 만족도를 높이는 상품 구성',
                    items: ['평면 및 타입 구성', '커뮤니티 시설 계획', '마감재 및 옵션 선정', '브랜드 전략'],
                  },
                ].map((item, index) => (
                  <AnimatedSection key={index} animation="scaleUp" delay={index * 150}>
                    <div className="bg-gray-50 rounded-xl overflow-hidden border border-gray-100 hover:shadow-lg transition-all hover:-translate-y-1 group h-full">
                      <div className="h-16 bg-navy flex flex-col items-center justify-center relative overflow-hidden">
                        <i className={`${item.icon} text-2xl text-gold relative z-10 mb-0.5`}></i>
                        <div className="text-[10px] text-white/70 relative z-10 font-english">{item.en}</div>
                      </div>
                      <div className="p-3">
                        <h3 className="text-lg font-bold text-navy mb-0.5 break-words">{item.ko}</h3>
                        <p className="text-[10px] text-gold font-semibold mb-0.5 break-words">{item.desc}</p>
                        <p className="text-[10px] text-gray-500 mb-2 break-words">{item.subdesc}</p>
                        <ul className="space-y-1">
                          {item.items.map((subItem, subIndex) => (
                            <li key={subIndex} className="text-[10px] text-gray-600 flex items-start gap-1">
                              <span className="w-1 h-1 bg-gold rounded-full mt-1 flex-shrink-0"></span>
                              <span className="break-words">{subItem}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
              <AnimatedSection animation="scaleUp" delay={300}>
                <div className="w-[calc(50vw-1.25rem)] max-w-[180px] bg-gray-50 rounded-xl overflow-hidden border border-gray-100 hover:shadow-lg transition-all hover:-translate-y-1 group">
                  <div className="h-16 bg-navy flex flex-col items-center justify-center relative overflow-hidden">
                    <i className="ri-rocket-line text-2xl text-gold relative z-10 mb-0.5"></i>
                    <div className="text-[10px] text-white/70 relative z-10 font-english">MARKETING</div>
                  </div>
                  <div className="p-3">
                    <h3 className="text-lg font-bold text-navy mb-0.5 break-words">영업기획</h3>
                    <p className="text-[10px] text-gold font-semibold mb-0.5 break-words">Pre-Salesmanship</p>
                    <p className="text-[10px] text-gray-500 mb-2 break-words">실질적인 구매 전환 유도</p>
                    <ul className="space-y-1">
                      {['통합 마케팅 전략', '매체 믹스 및 광고', '모델하우스 운영', '계약 및 사후 관리'].map((subItem, subIndex) => (
                        <li key={subIndex} className="text-[10px] text-gray-600 flex items-start gap-1">
                          <span className="w-1 h-1 bg-gold rounded-full mt-1 flex-shrink-0"></span>
                          <span className="break-words">{subItem}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </AnimatedSection>
            </div>

            {/* 데스크톱: 3열 그리드 */}
            <div className="hidden md:grid md:grid-cols-3 gap-4 lg:gap-6">
              {[
                {
                  en: 'PLANNING',
                  ko: '전략기획',
                  icon: 'ri-lightbulb-flash-line',
                  desc: '합리적인 분양 전략 수립',
                  subdesc: '인·허가 조건을 고려한 사업 기획',
                  items: ['시장 분석 및 타겟 설정', '분양가 전략', '차별화 포인트 발굴', '분양 일정 수립'],
                },
                {
                  en: 'CREATIVE',
                  ko: '구성기획',
                  icon: 'ri-palette-line',
                  desc: '상품 경쟁력 강화',
                  subdesc: '고객 만족도를 높이는 상품 구성',
                  items: ['평면 및 타입 구성', '커뮤니티 시설 계획', '마감재 및 옵션 선정', '브랜드 전략'],
                },
                {
                  en: 'MARKETING',
                  ko: '영업기획',
                  icon: 'ri-rocket-line',
                  desc: 'Pre-Salesmanship',
                  subdesc: '실질적인 구매 전환 유도',
                  items: ['통합 마케팅 전략', '매체 믹스 및 광고', '모델하우스 운영', '계약 및 사후 관리'],
                },
              ].map((item, index) => (
                <AnimatedSection key={index} animation="scaleUp" delay={index * 150}>
                  <div className="bg-gray-50 rounded-2xl overflow-hidden border border-gray-100 hover:shadow-lg transition-all hover:-translate-y-1 group">
                    <div className="h-20 lg:h-24 bg-navy flex flex-col items-center justify-center relative overflow-hidden">
                      <i className={`${item.icon} text-3xl lg:text-4xl text-gold relative z-10 mb-1`}></i>
                      <div className="text-xs text-white/70 relative z-10 font-english">{item.en}</div>
                    </div>
                    <div className="p-4 lg:p-6">
                      <h3 className="text-xl lg:text-2xl font-bold text-navy mb-1 break-words">{item.ko}</h3>
                      <p className="text-xs text-gold font-semibold mb-0.5 break-words">{item.desc}</p>
                      <p className="text-xs text-gray-500 mb-3 lg:mb-4 break-words">{item.subdesc}</p>
                      <ul className="space-y-1.5 lg:space-y-2">
                        {item.items.map((subItem, subIndex) => (
                          <li key={subIndex} className="text-xs text-gray-600 flex items-start gap-1.5">
                            <span className="w-1 h-1 bg-gold rounded-full mt-1.5 flex-shrink-0"></span>
                            <span className="break-words">{subItem}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* 5. MARKETING STRATEGY */}
        <section className="bg-[#FAF9F6] py-12 md:py-20">
          <div className="max-w-[1200px] mx-auto px-4 md:px-8">
            <AnimatedSection animation="fadeUp">
              <div className="mb-2">
                <span className="text-xs text-gold font-semibold tracking-wider font-english">PART 02. 사업영역</span>
              </div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy mb-1 font-english">MARKETING STRATEGY</h2>
              <p className="text-gold text-sm md:text-base mb-8 md:mb-10">마케팅 전략</p>
            </AnimatedSection>

            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2.5 md:gap-3 lg:gap-4">
              {[
                {
                  step: 1,
                  title: 'Operations',
                  subtitle: '기초 세팅',
                  icon: 'ri-search-line',
                  items: [
                    '지역 기초 조사 및 실사',
                    '집중 공략 업체 및 지역 설정',
                    '커뮤니케이션 목표 설정',
                    'IMAGE 구축',
                    '기업 IMAGE LAY-OUT 설정',
                    '사무실 차트 및 브리핑보드 완성',
                    '세부 일정 계획 수립',
                    '직원 브리핑 교육',
                    '홍보물 기획',
                    'D/M 발송 자료 SETTING',
                    '현지 아르바이트 요원 모집',
                  ],
                },
                {
                  step: 2,
                  title: 'Operations',
                  subtitle: '집중 분양',
                  icon: 'ri-lightbulb-line',
                  items: [
                    '전조직 영업활동 확대',
                    '홍보물 제작 완료',
                    '집중 공략지역 및 업체 우선 작업 시도',
                    '전단 배포작업 본격 시도',
                    'D/M에 의한 판매촉진',
                    'T/M 작업 시도',
                    '업소 영업활동 본격적 시도',
                    '홍보에 의한 방문자 상담 및 분양 유도',
                    '연고 판매 독려 및 결과 분석',
                    '연고, 방문자 담당에 의한 고객관리 구체화',
                    '중간평가',
                    'A, B, C, MARKETING 전개',
                  ],
                },
                {
                  step: 3,
                  title: 'Operations',
                  subtitle: '분양 극대화',
                  icon: 'ri-settings-3-line',
                  items: [
                    '분양 총력전 전개',
                    '목표 달성 가능성 점검',
                    '집중 공략지 TARGET 재점검',
                    'D/M에 의한 상담 및 방문',
                    'T/M 및 방문 상담',
                    '전단작업 및 이동 상담소 운용',
                    '전직원 현장 투입',
                    '분양가능자 선별 계약 유도',
                    '분양에 따른 제반 내용 검토 및 분석',
                    '성공적인 분양을 위한 지속적 방법 연구',
                  ],
                },
                {
                  step: 4,
                  title: 'Operations',
                  subtitle: '마무리 및 관리',
                  icon: 'ri-focus-3-line',
                  items: [
                    '모델하우스 분양 준비사항 점검',
                    '컴퓨터 분양자료 시스템 점검',
                    '분양계약서 및 영수증 준비 점검',
                    '홍보, 기획광고, 영업에 따른 제반 준비사항',
                    '집기 비품 및 기본 장부 준비',
                    '사무실 및 업무관리 체계적 계획서',
                    '분양 현장 기존 업체 상황 분석 (분양률, 제반활동, 준비사항 등)',
                    '광고 및 홍보물 제작',
                  ],
                },
              ].map((item, index) => (
                <AnimatedSection key={index} animation="fadeUp" delay={index * 100}>
                  <div
                    className={`bg-white rounded-lg overflow-hidden border-2 transition-all duration-300 cursor-pointer ${
                      selectedMarketingStep === item.step
                        ? 'border-gold shadow-lg'
                        : 'border-gray-200 hover:border-gold/50 hover:shadow-md'
                    }`}
                    onClick={() => setSelectedMarketingStep(selectedMarketingStep === item.step ? null : item.step)}
                  >
                    <div className="bg-navy px-2.5 md:px-3 lg:px-4 py-2.5 md:py-3 lg:py-4 relative">
                      <div className="flex items-center gap-1 md:gap-1.5 mb-0.5">
                        <span className="text-gold font-bold text-xs md:text-sm lg:text-base">{item.step}단계</span>
                        <span className="text-white/80 text-[10px] md:text-xs font-english">{item.title}</span>
                      </div>
                      <div className="absolute -bottom-1.5 md:-bottom-2 left-2.5 md:left-3 lg:left-4 w-2.5 h-2.5 md:w-3 md:h-3 lg:w-4 lg:h-4 bg-navy transform rotate-45"></div>
                    </div>

                    <div className="pt-2.5 md:pt-3 lg:pt-4 pb-2 md:pb-3 px-2.5 md:px-3 lg:px-4">
                      <ul className={`space-y-0.5 md:space-y-1 overflow-hidden transition-all duration-300 ${
                        selectedMarketingStep === item.step ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                      }`}>
                        {item.items.map((listItem, listIndex) => (
                          <li key={listIndex} className="text-[10px] md:text-xs text-gray-700 flex items-start gap-1 md:gap-1.5">
                            <span className="text-navy font-bold mt-0.5 text-[10px] md:text-xs flex-shrink-0">■</span>
                            <span className="break-words">{listItem}</span>
                          </li>
                        ))}
                      </ul>

                      <div className={`flex justify-center transition-all duration-300 ${
                        selectedMarketingStep === item.step ? 'mt-2.5 md:mt-3 lg:mt-4 pt-2 md:pt-3 border-t border-gray-100' : 'mt-0'
                      }`}>
                        <div className="w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 flex items-center justify-center">
                          <i className={`${item.icon} text-xl md:text-2xl lg:text-3xl text-gold`}></i>
                        </div>
                      </div>

                      <div className={`text-center mt-1 md:mt-1.5 transition-opacity duration-300 ${
                        selectedMarketingStep === item.step ? 'opacity-0 h-0' : 'opacity-100'
                      }`}>
                        <span className="text-[10px] md:text-xs text-gray-400">Click</span>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default BusinessPage;
